/**
 * Tema visual de los dashboards del Creador.
 * «Clásico» es el aspecto de siempre; «Ejecutivo» aplica un acabado limpio
 * (tipo Looker/Stripe) sin tocar los estilos que el creador eligió a mano: solo
 * reemplaza los valores que siguen en su default. Incluye la paleta categórica
 * validada (CVD ΔE ≥ 9.2, visión normal ≥ 20.8) y utilidades de formato de ejes.
 */
import { createContext, useContext } from 'react';

export const REPORT_THEMES = [
    { value: 'executive', label: 'Ejecutivo' },
    { value: 'classic', label: 'Clásico' },
];

/** Tema de reportes sin tema guardado (los existentes conservan su aspecto). */
export const LEGACY_REPORT_THEME = 'classic';
/** Tema de los reportes nuevos. */
export const NEW_REPORT_THEME = 'executive';

export function normalizeReportTheme(value, fallback = LEGACY_REPORT_THEME) {
    return value === 'executive' || value === 'classic' ? value : fallback;
}

export const DashboardThemeContext = createContext(LEGACY_REPORT_THEME);

/** Tema del dashboard donde está el widget ('classic' | 'executive'). */
export function useDashboardTheme() {
    return useContext(DashboardThemeContext);
}

/** Tokens del tema ejecutivo: tinta, superficies, líneas y sombras. */
export const EXEC = {
    ink: '#1f2430',
    muted: '#5b6270',
    faint: '#8a919c',
    grid: '#eceef2',
    line: '#e6e8ec',
    lineSoft: '#f0f1f4',
    surface: '#ffffff',
    headerBg: '#f7f7f9',
    stripe: '#fafafc',
    accent: '#5E35B1',
    accentSoft: '#efe9fa',
    cardBorder: '#e9ebef',
    cardShadow: '0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06)',
    tooltipShadow: '0 8px 24px rgba(16, 24, 40, 0.12), 0 2px 6px rgba(16, 24, 40, 0.06)',
    fontSize: 12,
};

/**
 * Paleta categórica del tema ejecutivo: morado de marca primero y el resto en un
 * orden validado (scripts/validate_palette.js de la skill dataviz, modo claro).
 * Los colores siguen a la entidad, nunca al rango.
 */
export const EXEC_PALETTE = ['#5E35B1', '#eb6834', '#1baf7a', '#2a78d6', '#e34948', '#eda100', '#008300', '#e87ba4'];

/** Máximo de rebanadas de una dona antes de agrupar el resto en «Otros». */
export const PIE_MAX_SLICES = 7;
export const OTHERS_LABEL = 'Otros';
/** Grosor máximo de barra (px): la franja sobrante queda como aire. */
export const EXEC_MAX_BAR = 28;

const compactFormatter = new Intl.NumberFormat('es-MX', { notation: 'compact', maximumFractionDigits: 1 });

/** 1234567 → «1.2 M»; valores chicos quedan como están. */
export function compactNumber(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return value;
    if (Math.abs(n) < 1000) return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 }).format(n);
    return compactFormatter.format(n);
}

// aaaa-mm-dd o solo aaaa-mm (periodos como «2026-03» de las hojas de medios).
const ISO_DATE = /^(\d{4})-(\d{2})(?:-(\d{2}))?(?:T|\s|$)/;
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

export function isIsoDate(value) {
    return typeof value === 'string' && ISO_DATE.test(value);
}

/** true si todos los valores (no vacíos) de la categoría son fechas ISO o periodos año-mes. */
export function isDateCategory(values = []) {
    const present = values.filter(v => v != null && v !== '' && v !== '(Sin dato)');
    return present.length > 0 && present.every(isIsoDate);
}

/**
 * Formateador de etiquetas para una categoría de fechas:
 * todo día 1 → «mar 2026»; si no, «5 mar» (con año si hay varios años).
 */
export function dateCategoryFormatter(values = []) {
    const parsed = values.filter(isIsoDate).map(v => v.match(ISO_DATE));
    const monthly = parsed.length > 0 && parsed.every(m => (m[3] || '01') === '01');
    const years = new Set(parsed.map(m => m[1]));
    return (value) => {
        const m = typeof value === 'string' ? value.match(ISO_DATE) : null;
        if (!m) return value ?? '';
        const month = MONTHS[Number(m[2]) - 1] || m[2];
        if (monthly) return `${month} ${m[1]}`;
        const day = String(Number(m[3] || '01'));
        return years.size > 1 ? `${day} ${month} ${m[1].slice(2)}` : `${day} ${month}`;
    };
}

/**
 * Orden por defecto de filas de una gráfica cuando el creador no eligió uno:
 * categorías de fecha → cronológico; lo demás → métrica de mayor a menor.
 * «(Sin dato)» y la fila «Otros» siempre al final.
 */
export function defaultChartOrder(rows, categoryKey, metricKey) {
    const list = [...rows];
    const blankLast = (a, b) => {
        const ab = a?.[categoryKey] === '(Sin dato)' || isOthersRow(a);
        const bb = b?.[categoryKey] === '(Sin dato)' || isOthersRow(b);
        return ab === bb ? 0 : (ab ? 1 : -1);
    };
    if (categoryKey && isDateCategory(list.map(row => row[categoryKey]))) {
        return list.sort((a, b) => blankLast(a, b) || String(a[categoryKey] ?? '').localeCompare(String(b[categoryKey] ?? '')));
    }
    if (!metricKey) return list;
    return list.sort((a, b) => {
        const blank = blankLast(a, b);
        if (blank) return blank;
        const av = a[metricKey];
        const bv = b[metricKey];
        if (typeof av === 'number' && typeof bv === 'number') return bv - av;
        return String(bv ?? '').localeCompare(String(av ?? ''), 'es');
    });
}

/**
 * Rebanadas de una dona: si hay más de PIE_MAX_SLICES, las menores se suman en «Otros».
 * Recibe filas ya ordenadas de mayor a menor.
 */
export function foldPieSlices(rows, categoryKey, metricKey, max = PIE_MAX_SLICES) {
    if (!Array.isArray(rows) || rows.length <= max) return rows;
    const keep = rows.slice(0, max - 1);
    const rest = rows.slice(max - 1);
    const total = rest.reduce((sum, row) => sum + (Number(row[metricKey]) || 0), 0);
    return [...keep, { [categoryKey]: OTHERS_LABEL, [metricKey]: total, __others: rest.length }];
}

/** Valores por defecto de estilo de tabla que el tema ejecutivo sustituye (si no se cambiaron). */
export const CLASSIC_TABLE_DEFAULTS = {
    headerBackground: '#5E35B1',
    headerColor: '#ffffff',
    stripeBackground: '#f6f2fb',
    dimTextColor: '#4a148c',
    totalsBackground: '#ede7f6',
    totalsColor: '#4a148c',
};

/** Usa el valor del creador si lo cambió; si sigue en el default clásico, el del tema. */
export function themed(value, classicDefault, executiveValue, isExecutive) {
    if (!isExecutive) return value || classicDefault;
    if (!value || String(value).toLowerCase() === String(classicDefault).toLowerCase()) return executiveValue;
    return value;
}

/** Fila «Otros» que arma el motor con el resto del Top N. */
export function isOthersRow(row) {
    return Boolean(row) && row.__others != null;
}

/**
 * Etiqueta de una fecha ya agrupada por el motor (inicio del periodo en ISO):
 * día «5 mar» · semana «sem 5 mar» · mes «mar 2026» · trimestre «T1 2026» · año «2026».
 */
export function grainLabelFormatter(grain) {
    return (value) => {
        const m = typeof value === 'string' ? value.match(ISO_DATE) : null;
        if (!m) return value ?? '';
        const [, y, mo, dRaw] = m;
        const d = dRaw || '01';
        const month = MONTHS[Number(mo) - 1] || mo;
        if (grain === 'year') return y;
        if (grain === 'quarter') return `T${Math.floor((Number(mo) - 1) / 3) + 1} ${y}`;
        if (grain === 'month') return `${month} ${y}`;
        if (grain === 'week') return `sem ${Number(d)} ${month}`;
        return `${Number(d)} ${month}`;
    };
}

/** Separa la fila «Otros» para que ordenar o cortar el Top N no la mueva ni la quite. */
export function splitOthers(rows = []) {
    const others = rows.filter(isOthersRow);
    return { rows: others.length ? rows.filter(row => !isOthersRow(row)) : rows, others };
}
